import { CircleCircle } from '@calm/evexia-web-sdk';
import createPollApi from '@rhg/libs/api/create-poll-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import { queryKeys, useQuery } from '@rhg/libs/react-query';
import { useActiveCircle } from '../use-active-circle';

export const pollQueryOptions = {
    queryKey: function (pollId: string) {
        return [queryKeys.poll, pollId];
    },
    queryFn: async function (
        getAccessTokenSilently: () => Promise<string>,
        pollId: string,
        isAuthenticated: boolean,
        activeCircle?: CircleCircle
    ) {
        const pollApi = createPollApi(getAccessTokenSilently);

        // When the user is authenticated we need to call the endpoint associated with the user's circle to get the right questions.
        if (
            isAuthenticated &&
            typeof activeCircle?.recipient.id === 'number' &&
            !!activeCircle.authorizationJWT
        ) {
            const response =
                await pollApi.getV1PollsRecipientsRecipientIdPollPollIdConfig({
                    pollId,
                    recipientId: activeCircle.recipient.id,
                    circleAuthorizationToken: activeCircle.authorizationJWT,
                });

            return response.data;
        }
    },
    queryOptions: function (
        pollId: string,
        isAuthenticated: boolean,
        activeCircle?: CircleCircle
    ) {
        return {
            enabled: !!(
                isAuthenticated &&
                pollId &&
                typeof activeCircle?.recipient.id === 'number' &&
                !!activeCircle.authorizationJWT
            ),
        };
    },
};

export function usePoll(pollId = '') {
    const { activeCircle } = useActiveCircle();
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const { queryKey, queryFn, queryOptions } = pollQueryOptions;

    const { data, ...otherProps } = useQuery({
        queryKey: queryKey(pollId),
        queryFn: async () =>
            queryFn(
                getAccessTokenSilently,
                pollId,
                isAuthenticated,
                activeCircle
            ),
        ...queryOptions(pollId, isAuthenticated, activeCircle),
    });
    return {
        poll: data,
        ...otherProps,
    };
}
